import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { usePondsState } from 'views/production-systems/context/ponds-context';

import { Pond } from 'views/production-systems/types/pond.type';

import { Box, Button, CircularProgress, MobileStepper } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import DataGridAutocomplete, { Option } from 'components/data-grid/data-grid-autocomplete';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import { renderDataGridOption } from 'components/data-grid/render-data-grid-option';
import FlexBox from 'components/flex-box';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { usePost } from 'hooks/use-post';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { isValidDecimal } from 'utils/validate-numbers';
import { postTreatmentRegistration } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { TreatmentProvider, useTreatmentState } from 'views/production-systems/context/treatment-context';
import { CreateTreatmentInput } from 'views/production-systems/features/farm-overview/types/create-treatment-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { Treatment } from 'views/production-systems/types/treatment.type';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import RegistrationHeader from '../../../../components/registration-header';
import ListSelectedPonds from '../list-selected-ponds';
import DuplicateRegistrationWarning, { REGISTRATION_EXISTS_QUERY_KEY } from '../duplicate-registration-warning';
import { RegistrationActionType } from '../../types/registration-action-type.enum';

type Row = {
  pondId: string;
  pondName: string;
  pondStatus: boolean;
  cycleName: string;
  cycleId: number;
  zoneId: string;
  zoneName: string;
  currentQty: number;
  currentMw: number;
  currentBm: number;
  treatment: { id: string; label: string };
  value: number;
};

type Props = {
  ponds: Pond[];
  handleClose?: () => void;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.treatment.add';

const AddTreatment: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const { treatments } = useTreatmentState();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, registrationDate } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  // #region STATE

  const [confirm, setConfirm] = useState<boolean>(false);
  const [selectedPonds, setSelectedPonds] = useState<Pond[]>(ponds);

  // #endregion STATE

  //#region FETCH

  const treatmentOptions: Option[] = useMemo(() => {
    return (
      treatments
        ?.filter((t) => !t.hidden)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        .map((d) => ({ id: String(d.id), label: d.name })) || []
    );
  }, [treatments]);

  //#endregion

  //#region POST DATA

  const createTreatment = usePost<Treatment, { globalSiteId: string; request: CreateTreatmentInput }>(
    (request) => postTreatmentRegistration(request.globalSiteId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: `snackbar.success.default` }) },
      });
      !!handleClose && handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion POST DATA

  //#region DATA GRID

  const initialRows = useMemo(() => {
    return selectedPonds.map((pond) => ({
      cycleId: pond.cycleId,
      pondName: pond.pondName,
      pondId: pond.pondId,
      pondStatus: pond.cycleId !== 0,
      zoneName: pond.zoneName,
      zoneId: pond.zoneId,
      cycleName: pond.cycleName,
      currentMw: pond.currentMw,
      currentQty: pond.currentQty,
      currentBm: calculateBiomass(pond.currentMw, pond.currentQty),
      treatment: { id: '', label: '' },
      value: 0,
    }));
  }, [selectedPonds]);

  const [rows, setRows] = useState(initialRows || []);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destinationPond` }),
        editable: false,
        renderCell: ({ row }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                width: '100%',
              }}>
              {row.pondStatus ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'cycleName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.cycleName` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'zoneName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.zone` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'currentQty',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.number` }),
        type: 'number',
        editable: false,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentMw',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.meanWeight` }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentBm',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.biomass` }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'treatment',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.treatmentType` }),
        flex: 1,
        type: 'singleSelect',
        valueOptions: treatmentOptions,
        valueFormatter: (value: Option) => value?.label,
        renderCell: renderDataGridOption,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value === '';
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-treatment`]: hasError });
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => <DataGridAutocomplete params={params} options={treatmentOptions} />,
        sortable: false,
        editable: true,
      } as GridColDef<any, { id: number; label: string }, string>,
      {
        field: 'value',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.amount` }),
        editable: true,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-value`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
    ];
  }, [treatmentOptions]);

  const summaryValues: SummaryItem[] = useMemo(() => {
    let newSummary = rows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.currentMw, curr.currentQty);

        return {
          currentQty: prev.currentQty + curr.currentQty,
          currentBm: prev.currentBm + biomass,
          currentMw: 0,
          value: prev.value + curr.value,
        };
      },
      {
        currentQty: 0,
        currentBm: 0,
        currentMw: 0,
        value: 0,
      }
    );

    newSummary.currentMw = calculateMeanWeight(newSummary.currentBm, newSummary.currentQty);

    return [
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.currentQty,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentMw, // The value remains the number itself
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3, // Derived from .toFixed(3)
      },
      {
        value: newSummary.currentBm, // The value remains the number itself
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3, // Derived from .toFixed(3)
      },
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.value, // The value remains the number itself
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3, // Derived from .toFixed(3)
      },
    ];
  }, [rows]);

  //#endregion DATA GRID

  //#region HANDLERS

  const [activeStep, setActiveStep] = React.useState(0);
  const isCheckingDuplicates = useIsFetching({ queryKey: [REGISTRATION_EXISTS_QUERY_KEY] }) > 0;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  const handleSubmit = () => {
    let request = rows.map((row) => ({
      treatmentId: row.treatment!.id,
      amount: row.value || 0,
      cycleId: row.cycleId,
      pondId: row.pondId,
    }));

    createTreatment.mutate({
      globalSiteId: globalSiteId,
      request: {
        cycles: request,
        date: registrationDate.format('YYYY-MM-DD'),
      },
    });
  };

  //#endregion HANDLERS

  //#region HOOKS

  const disableSubmit = useMemo(() => {
    return rows.some((row) => row.treatment.id === '' || !isValidDecimal(row.value)) ? true : false;
  }, [rows]);

  //#endregion HOOKS

  return lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      {activeStep === 0 && (
        <FlexBox>
          <ListSelectedPonds selectedPonds={selectedPonds} setSelectedPonds={setSelectedPonds} />
        </FlexBox>
      )}
      {activeStep === 1 && (
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <DuplicateRegistrationWarning actionTypeId={RegistrationActionType.Treatment} ponds={selectedPonds} />
          <RegistrationHeader />
          <DataGrid
            rows={rows}
            apiRef={apiRef}
            columns={columns}
            getRowId={(row) => row.pondId}
            processRowUpdate={processRowUpdate}
            slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
            showToolbar
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: `${intl.formatMessage({
                    id: 'productionSystems.registrationLog.registrationTypes.treatment',
                  })}-${dayjs().format('DD/MM/YYYY')}`,
                },
              },
              footer: {
                summaryValues: summaryValues,
              } as DataGridSummaryProps,
            }}
            onCellEditStop={({ field }) => {
              if (field === 'treatment') return;
              handleEditing();
            }}
            onRowClick={({ id }) => {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              if (field === 'treatment') return;
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
          />
        </Box>
      )}
      <MobileStepper
        steps={2}
        position="static"
        activeStep={activeStep}
        nextButton={
          activeStep === 1 ? (
            <Button
              disabled={createTreatment.isPending || disableSubmit || isEditing || hasError || isCheckingDuplicates}
              onClick={handleSubmit}
              startIcon={createTreatment.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.submit" values={{ type: '' }} />
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={activeStep === 1}>
              <FormattedMessage id="general.next" />
            </Button>
          )
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <FormattedMessage id="general.back" />
          </Button>
        }
      />
    </Box>
  );
};

const TreatmentContainer: React.FC<Props> = ({ ...props }) => {
  return (
    <TreatmentProvider>
      <AddTreatment {...props} />
    </TreatmentProvider>
  );
};

export default TreatmentContainer;
